import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, parseISO } from 'date-fns';
import { Calendar as CalIcon, Moon, Sun, Users, ArrowRight, Loader2, Check } from 'lucide-react';
import { publicGet } from '../../lib/publicApi';

export default function QuickBookWidget({ property }) {
  const navigate = useNavigate();

  const [sessionType, setSessionType] = useState('night');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);

  const minDate = format(new Date(), 'yyyy-MM-dd');
  const minOut = useMemo(
    () => checkIn ? format(addDays(parseISO(checkIn), 1), 'yyyy-MM-dd') : minDate,
    [checkIn, minDate]
  );

  // Auto-update checkout when checkin or session changes
  useEffect(() => {
    if (sessionType === 'night' && checkIn && (!checkOut || checkOut <= checkIn)) {
      setCheckOut(format(addDays(parseISO(checkIn), 1), 'yyyy-MM-dd'));
    }
    if (sessionType === 'dayuse' && checkIn) setCheckOut(checkIn);
  }, [sessionType, checkIn]); // eslint-disable-line

  // Live price preview
  useEffect(() => {
    if (!checkIn || !property?.id) return;
    const outDate = sessionType === 'dayuse' ? checkIn : checkOut;
    if (!outDate) return;
    setLoading(true);
    const params = new URLSearchParams({ check_in: checkIn, check_out: outDate, session_type: sessionType });
    publicGet(`/api/guest/property/${property.id}/availability?${params.toString()}`)
      .then(setPricing)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [checkIn, checkOut, sessionType, property?.id]);

  if (!property) return null;

  const handleReserve = () => {
    const outDate = sessionType === 'dayuse' ? checkIn : checkOut;
    const params = new URLSearchParams();
    params.set('session', sessionType);
    if (checkIn) params.set('checkin', checkIn);
    if (outDate) params.set('checkout', outDate);
    params.set('guests', String(guests));
    if (checkIn && pricing?.available !== false) params.set('skip', '1');
    navigate(`/reserver/${property.id}?${params.toString()}`);
  };

  const nightPrice = property.night_price ?? property.base_price ?? 100;
  const dayusePrice = property.dayuse_price ?? 120;

  return (
    <div className="w-full max-w-2xl mx-auto glass-dark border-gold-soft p-5 md:p-6 mt-10 animate-fade-in stagger-4" data-testid="quick-book-widget">
      {/* Session toggle */}
      <div className="grid grid-cols-2 gap-2 mb-5">
        {[
          { key: 'night', icon: Moon, label: 'Nuit', price: nightPrice, enabled: property.night_enabled !== false },
          { key: 'dayuse', icon: Sun, label: 'Day Use', price: dayusePrice, enabled: property.dayuse_enabled !== false },
        ].filter(s => s.enabled).map((s) => {
          const Icon = s.icon;
          const active = sessionType === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSessionType(s.key)}
              className={`flex items-center justify-center gap-2 py-3 border text-xs uppercase tracking-[0.22em] font-body transition-colors ${active ? 'border-[#D4AF37] bg-[#D4AF37] text-[#0D0C0B]' : 'border-white/15 text-[#F5F2EB] hover:border-[#D4AF37]/50'}`}
              data-testid={`qb-session-${s.key}`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {s.label} · €{s.price}
            </button>
          );
        })}
      </div>

      {/* Dates */}
      <div className={`grid ${sessionType === 'night' ? 'grid-cols-2' : 'grid-cols-1'} gap-2 mb-3`}>
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase tracking-[0.22em] text-[#A39E93] font-body">
            {sessionType === 'night' ? 'Arrivée' : 'Date'}
          </label>
          <div className="relative">
            <CalIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
            <input
              type="date" min={minDate} value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
              className="w-full bg-[#0D0C0B] border border-white/10 focus:border-[#D4AF37] py-3 pl-9 pr-3 font-body text-sm text-[#F5F2EB] outline-none transition-colors"
              data-testid="qb-checkin"
            />
          </div>
        </div>
        {sessionType === 'night' && (
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#A39E93] font-body">Départ</label>
            <div className="relative">
              <CalIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
              <input
                type="date" min={minOut} value={checkOut}
                onChange={(e) => setCheckOut(e.target.value)}
                className="w-full bg-[#0D0C0B] border border-white/10 focus:border-[#D4AF37] py-3 pl-9 pr-3 font-body text-sm text-[#F5F2EB] outline-none transition-colors"
                data-testid="qb-checkout"
              />
            </div>
          </div>
        )}
      </div>

      {/* Guests */}
      <div className="mb-4">
        <label className="text-[10px] uppercase tracking-[0.22em] text-[#A39E93] font-body block mb-2">Voyageurs</label>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4].slice(0, property.max_guests || 2).map(n => (
            <button
              key={n} onClick={() => setGuests(n)}
              className={`w-10 h-10 border font-body text-sm tabular-nums transition-colors ${guests === n ? 'border-[#D4AF37] bg-[#D4AF37] text-[#0D0C0B]' : 'border-white/10 hover:border-[#D4AF37]/40 text-[#F5F2EB]'}`}
              data-testid={`qb-guests-${n}`}
            >
              {n}
            </button>
          ))}
          <Users className="w-3.5 h-3.5 text-[#A39E93] ml-2" />
        </div>
      </div>

      {/* Price preview */}
      {pricing && checkIn && (
        <div className="flex items-baseline justify-between border-t border-white/10 pt-4 mb-4" data-testid="qb-price-preview">
          <div className="flex items-center gap-2">
            {pricing.available ? (
              <Check className="w-4 h-4 text-[#D4AF37]" />
            ) : null}
            <span className="text-xs text-[#A39E93] font-body">
              {pricing.available ? (sessionType === 'night' ? `${pricing.nights} nuit${pricing.nights > 1 ? 's' : ''}` : 'Day Use') : 'Indisponible'}
            </span>
          </div>
          {pricing.available && (
            <span className="font-display text-2xl text-[#D4AF37] tabular-nums">€{pricing.total_price?.toFixed(2)}</span>
          )}
        </div>
      )}

      <button
        onClick={handleReserve}
        disabled={loading || (pricing && pricing.available === false)}
        className="w-full btn-luxury justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="qb-reserve-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>{checkIn && pricing?.available !== false ? 'Réserver maintenant' : 'Voir la disponibilité'} <ArrowRight className="ml-3 w-4 h-4" /></>
        )}
      </button>
    </div>
  );
}
